import { useEffect, useState } from 'react'
import CsrfTokenTag from '../../../../lib/CsrfTokenTag'
import { colors } from '../../../colors'
import { FREE_THEME_ID } from '../../../constants'
import themeButtonImage from '../../../images/theme_button.svg'
import ErrorMessage from '../ErrorMessage'
import CommentField from '../PostForm/fields/CommentField'
import PriceField from '../PostForm/fields/PriceField'
import ProductNameField from '../PostForm/fields/ProductNameField'
import ShopSearchField from '../PostForm/fields/ShopSearchField'
import UserInfoDisplay from '../PostForm/fields/UserInfoDisplay'
import { useImageProcessing } from '../PostForm/hooks/useImageProcessing'
import { usePostForm } from '../PostForm/hooks/usePostForm'
import { useShopSearch } from '../PostForm/hooks/useShopSearch'
import PostPreview from '../PostForm/PostPreview'
import PostSuccess from '../PostForm/PostSuccess'
import type { BargainUserInfo, Shop, Theme } from '../../../types'
import { createPostWithOnlySuccessStatus } from '@/components/MessageBoard/api'

interface FreeThemePostFormProps {
  theme: Theme
  bargain_user_info: BargainUserInfo
  myarea_shops: Shop[]
  currentTheme?: Theme
}

const styles: Record<string, React.CSSProperties> = {
  postForm: {
    maxWidth: '600px',
    margin: '0 auto',
    backgroundColor: colors.white,
    overflow: 'hidden',
  },
  headerSection: {
    backgroundColor: colors['keijiban-coral-bg'],
    padding: '20px 16px',
    textAlign: 'center',
  },
  formTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    margin: '0',
    color: colors['keijiban-brown'],
  },
  formDescription: {
    fontSize: '14px',
    fontWeight: '600',
    margin: '0',
    color: colors['keijiban-brown'],
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
  },
  formContent: {
    padding: '24px 16px',
  },
  formActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '24px',
  },
  submitButton: {
    padding: '16px 24px',
    backgroundColor: 'rgba(0,0,0,0.12)',
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    width: '100%',
  },
  submitButtonActive: {
    backgroundColor: colors.primary,
  },
  backButton: {
    padding: '16px 24px',
    backgroundColor: 'transparent',
    color: colors['text-secondary'],
    border: `1px solid ${colors['border-light']}`,
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
  previewTitleBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '24px',
  },
  previewBoxConfirmText: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: colors['text-default'],
    marginBottom: '8px',
  },
  previewTitle: {
    fontSize: '14px',
    fontWeight: 300,
    color: colors['keijiban-brown'],
    margin: '0',
  },
  disabledButton: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  entryButton: {
    display: 'flex',
    margin: '36px 0 0',
    padding: '0 11px',
    justifyContent: 'center',
  },
  entryButtonImage: {
    width: '100%',
    display: 'block',
  },
}

const FreeThemePostForm: React.FC<FreeThemePostFormProps> = ({
  theme,
  bargain_user_info,
  myarea_shops,
  currentTheme,
}) => {
  const [isPC, setIsPC] = useState(window.innerWidth > 640)

  useEffect(() => {
    const handleResize = () => {
      setIsPC(window.innerWidth > 640)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // 画像処理
  const {
    images,
    processedImages,
    imagePreviews,
    loading: imageLoading,
    handleImageChange,
    handleRemoveImage,
  } = useImageProcessing()

  // フォーム状態管理
  const {
    formData,
    isPreviewMode,
    isPostCompleted,
    loading,
    error,
    isFormValid,
    setLoading,
    setError,
    updateFormData,
    handleProductNameChange,
    handleCommentChange,
    handlePriceChange,
    handlePosterNameChange,
    handleShowPreview,
    handleBackToEdit,
    handlePostSuccess,
    validateAndSetError,
  } = usePostForm(bargain_user_info, images)

  // ショップ検索
  const {
    shopId,
    shopName,
    chainName,
    chainLogoUrl,
    searchQuery,
    searchResults,
    isSearching,
    isShopInputFocused,
    handleShopSearchChange,
    handleShopInputFocus,
    handleShopInputBlur,
    handleShopSelect,
  } = useShopSearch(myarea_shops)

  // ショップ選択時にフォームデータを更新
  useEffect(() => {
    updateFormData({ shopId })
  }, [shopId, updateFormData])

  const labelWidth = 90

  const hasInput =
    !!formData.shopId ||
    !!formData.productName ||
    !!formData.comment ||
    !!formData.price ||
    !!formData.posterName ||
    images.length > 0

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasInput && !isPostCompleted) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasInput, isPostCompleted])

  // フォーム送信ハンドラー
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isPreviewMode) {
      handleShowPreview()
      return
    }

    if (!validateAndSetError()) return

    try {
      setLoading(true)

      const formDataToSubmit = new FormData()

      formDataToSubmit.append('post[bargain_message_board_theme_id]', FREE_THEME_ID.toString())
      formDataToSubmit.append('post[bargain_shop_id]', shopId?.toString() || '')
      formDataToSubmit.append('post[product_name]', formData.productName)
      formDataToSubmit.append('post[content]', formData.comment)

      // ユーザー名がない場合は入力されたユーザー名を使用
      if (!bargain_user_info.name && formData.posterName) {
        formDataToSubmit.append('post[poster_name]', formData.posterName)
      }

      if (formData.price) {
        formDataToSubmit.append('post[purchased_price]', formData.price)
      }

      // 処理済み画像の追加（jpeg形式に変換済み）
      for (const image of processedImages) {
        formDataToSubmit.append('images[]', image)
      }

      await createPostWithOnlySuccessStatus(formDataToSubmit)
      handlePostSuccess()
    } catch {
      setError('投稿の作成に失敗しました。もう一度お試しください。')
      handleBackToEdit()
    } finally {
      setLoading(false)
    }
  }

  const handleImageChangeWithError = async (files: FileList) => {
    try {
      await handleImageChange(files)
    } catch (error) {
      setError(error instanceof Error ? error.message : '画像の処理中にエラーが発生しました。')
    }
  }

  const handleBack = () => {
    window.location.href = '/webview/message_board/posts'
  }

  return (
    <div style={styles.postForm}>
      {currentTheme && (
        <div
          style={{
            ...styles.entryButton,
          }}
        >
          <a
            href={`/webview/message_board/themes/${currentTheme.id}/posts/new`}
            style={{
              width: isPC ? '70%' : '100%',
            }}
          >
            <img
              src={themeButtonImage}
              alt="今週のお題に参加する方はこちら"
              style={{
                ...styles.entryButtonImage,
              }}
            />
          </a>
        </div>
      )}

      <div style={styles.formContent}>
        {error && <ErrorMessage message={error} />}

        <form onSubmit={handleSubmit} autoComplete="off">
          {!isPreviewMode ? (
            <>
              <ProductNameField
                value={formData.productName}
                onChange={handleProductNameChange}
                labelWidth={labelWidth}
              />

              <CommentField
                value={formData.comment}
                onChange={handleCommentChange}
                images={processedImages}
                imagePreviews={imagePreviews}
                loading={imageLoading}
                onImageChange={handleImageChangeWithError}
                onRemoveImage={handleRemoveImage}
                bargain_user_info={bargain_user_info}
                labelWidth={labelWidth}
              />

              <ShopSearchField
                searchQuery={searchQuery}
                searchResults={searchResults}
                isSearching={isSearching}
                isShopInputFocused={isShopInputFocused}
                onSearchChange={handleShopSearchChange}
                onInputFocus={handleShopInputFocus}
                onInputBlur={handleShopInputBlur}
                onShopSelect={handleShopSelect}
                labelWidth={labelWidth}
              />

              <PriceField
                value={formData.price}
                onChange={handlePriceChange}
                labelWidth={labelWidth}
              />

              <UserInfoDisplay
                bargain_user_info={bargain_user_info}
                posterName={formData.posterName}
                onPosterNameChange={handlePosterNameChange}
                labelWidth={labelWidth}
              />

              {/* フォームアクション */}
              <div style={styles.formActions}>
                <button
                  type="submit"
                  style={{
                    ...styles.submitButton,
                    ...(isFormValid && !loading
                      ? styles.submitButtonActive
                      : styles.disabledButton),
                  }}
                  disabled={!isFormValid || loading}
                >
                  {loading ? '投稿中...' : '内容を確認'}
                </button>
                <button
                  type="button"
                  onClick={handleBack}
                  style={styles.backButton}
                  disabled={loading}
                >
                  戻る
                </button>
              </div>
            </>
          ) : isPostCompleted ? (
            <PostSuccess />
          ) : (
            <>
              <div style={styles.previewTitleBox}>
                <div style={styles.previewBoxConfirmText}>この内容で投稿しますか？</div>
              </div>
              <PostPreview
                formData={formData}
                imagePreviews={imagePreviews}
                shopName={shopName}
                chainName={chainName}
                chainLogoUrl={chainLogoUrl}
                bargain_user_info={bargain_user_info}
                loading={loading}
                onBackToEdit={handleBackToEdit}
                onSubmit={() => {
                  const event = new Event('submit') as unknown as React.FormEvent
                  event.preventDefault = () => {}
                  void handleSubmit(event)
                }}
                currentTheme={theme}
                currentThemeId={FREE_THEME_ID}
              />
            </>
          )}
        </form>
        <CsrfTokenTag />
      </div>
    </div>
  )
}

export default FreeThemePostForm
